import {
  IconSparkles,
  IconDisc,
  IconVinyl,
  IconBook,
  IconBookmark,
  IconPhoto,
  type Icon,
} from "@tabler/icons-react"
import type { MediaResource, MediaVariant } from "./types"

// Human labels for the media variants — shown in the section/item editor's preview-style picker.
export const MEDIA_VARIANT_LABELS: Record<MediaVariant, string> = {
  auto: "Default (rich, by type)",
  sleeve: "Record & sleeve",
  vinyl: "Vinyl disc",
  spine: "Spine",
  cover: "Cover",
  plain: "Plain image",
}

// One glyph per variant, for the editor's preview-style picker (a small icon beside each option). Auto is the
// rich per-type treatment (sparkles); the rest hint at their shape — a disc & sleeve, a vinyl, a book cover, a
// spine, a plain photo.
export const MEDIA_VARIANT_ICONS: Record<MediaVariant, Icon> = {
  auto: IconSparkles,
  sleeve: IconDisc,
  vinyl: IconVinyl,
  spine: IconBookmark,
  cover: IconBook,
  plain: IconPhoto,
}

// Which variants each resource type sensibly offers, so the editor shows the right options per item (and a
// section can set a default). "auto" (rich, by type) is always first; "plain" (simple thumbnail) is always
// offered as the opt-out.
const AUDIO: MediaVariant[] = ["auto", "sleeve", "vinyl", "cover", "plain"]
const BOOKLIKE: MediaVariant[] = ["auto", "cover", "spine", "plain"]
const CODEX: MediaVariant[] = ["auto", "spine", "cover", "plain"]
const COVER_ONLY: MediaVariant[] = ["auto", "cover", "plain"]

// Keyed by normalized type (see typeKey). Unlisted types fall back to COVER_ONLY.
export const MEDIA_VARIANT_OPTIONS_BY_TYPE: Record<string, MediaVariant[]> = {
  // Audio — record & sleeve / vinyl disc
  song: AUDIO,
  music_album: AUDIO,
  music_video: AUDIO,
  podcast: AUDIO,
  radio_station: AUDIO,
  // Book-like — cover or spine
  book: BOOKLIKE,
  audiobook: BOOKLIKE,
  comic: BOOKLIKE,
  publication: BOOKLIKE,
  dictionary: BOOKLIKE,
  // Codex records stand as a labelled spine
  codex_record: CODEX,
  // Everything else is cover-only (movies/tv, apps, tools, courses, games, profiles, events, …)
  movie: COVER_ONLY,
  tv_show: COVER_ONLY,
  documentary: COVER_ONLY,
  video: COVER_ONLY,
  youtube_channel: COVER_ONLY,
  app: COVER_ONLY,
  tool: COVER_ONLY,
  website: COVER_ONLY,
  course: COVER_ONLY,
  flashcard_set: COVER_ONLY,
  game: COVER_ONLY,
  video_game: COVER_ONLY,
  board_game: COVER_ONLY,
  mini_game: COVER_ONLY,
  teacher: COVER_ONLY,
  community: COVER_ONLY,
  event: COVER_ONLY,
  calendar_event: COVER_ONLY,
  interview: COVER_ONLY,
  guestbook: COVER_ONLY,
  news_source: COVER_ONLY,
}

/**
 * Normalize a resource's type to the registry key: "ItemSong" / "Item::Song" / "songs" all -> "song".
 * Strips the Item namespace, camelCase -> snake_case, and a trailing plural.
 */
export const typeKey = (typeOrResource: string | MediaResource): string => {
  const raw =
    typeof typeOrResource === "string"
      ? typeOrResource
      : typeOrResource.__typename || typeOrResource.type || typeOrResource.libraryType || ""

  return raw
    .replace(/^Item::/, "")
    .replace(/^Item/, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/::/g, "_")
    .toLowerCase()
    .replace(/s$/, "")
}

/** The variant options a given resource type offers (for the editor picker). Always includes "auto". */
export const mediaVariantOptionsForType = (
  typeOrResource: string | MediaResource
): MediaVariant[] => MEDIA_VARIANT_OPTIONS_BY_TYPE[typeKey(typeOrResource)] ?? COVER_ONLY
